import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as Yup from 'yup';

import { makeStyles } from '@material-ui/core/styles';
import { useToasts } from 'react-toast-notifications';
import { CircularProgress } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';

import styles from 'assets/jss/material-dashboard-react/views/dashboardStyle.js';
import GridItem from 'components/Grid/GridItem.js';
import GridContainer from 'components/Grid/GridContainer.js';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import Card from 'components/Card/Card';
import Button from 'components/CustomButtons/Button.js';
import FileUploader from 'components/FileUploader/FileUploader';

import api from '../../services/api';
import getValidationErrors from '../../utils/getValidationErrors';

const useStyles = makeStyles(styles);

export default function EmailMarketing() {
  const classes = useStyles();
  const { addToast } = useToasts();

  const [isLoading, setLoading] = useState(true);
  const [isSubmitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);

  const [segment, setSegment] = useState('all');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.post('/customers/search', {
        filtros: {
          name: '',
          email: '',
          ordem: 'ordem_alfabetica',
        },
      });

      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      addToast('Nao foi possivel carregar os clientes cadastrados.', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const recipients = useMemo(() => {
    if (segment === 'all') {
      return customers.filter(customer => !!customer.email);
    }

    return customers.filter(
      customer =>
        !!customer.email && String(customer.type).toLowerCase() === segment,
    );
  }, [customers, segment]);

  const handleImageSelect = useCallback(file => {
    setSelectedImage(file);
    setImagePreviewUrl(currentPreviewUrl => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }

      return URL.createObjectURL(file);
    });
  }, []);

  const handleRemoveSelectedImage = useCallback(() => {
    setSelectedImage(null);
    setImagePreviewUrl(currentPreviewUrl => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }

      return '';
    });
  }, []);

  const handleSendCampaign = useCallback(
    async event => {
      try {
        event.preventDefault();

        const schema = Yup.object().shape({
          subject: Yup.string().required('Informe o assunto da campanha.'),
          message: Yup.string().required('Informe a mensagem da campanha.'),
        });

        await schema.validate(
          { subject, message },
          {
            abortEarly: false,
          },
        );

        if (!recipients.length) {
          addToast('Nao ha clientes com email valido para este segmento.', {
            appearance: 'error',
            autoDismiss: true,
          });
          return;
        }

        setSubmitting(true);

        const recipientsPayload = recipients.map(customer => ({
          id: customer.id,
          email: customer.email,
          name: customer.name,
        }));

        if (selectedImage) {
          const formData = new FormData();

          formData.append('subject', subject);
          formData.append('message', message);
          formData.append('segment', segment);
          formData.append('recipients', JSON.stringify(recipientsPayload));
          formData.append('image', selectedImage);

          await api.post('/email-marketing/send', formData);
        } else {
          await api.post('/email-marketing/send', {
            subject,
            message,
            segment,
            recipients: recipientsPayload,
          });
        }

        addToast('Campanha enviada com sucesso.', {
          appearance: 'success',
          autoDismiss: true,
        });

        setSubject('');
        setMessage('');
        handleRemoveSelectedImage();
      } catch (e) {
        if (e instanceof Yup.ValidationError) {
          const errors = getValidationErrors(e);

          for (const [, value] of Object.entries(errors)) {
            addToast(value, {
              appearance: 'error',
              autoDismiss: true,
            });
          }
          return;
        }

        addToast(
          e.response?.data?.message ||
            'Nao foi possivel enviar a campanha. Verifique o endpoint de disparo no backend.',
          {
            appearance: 'error',
            autoDismiss: true,
          },
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      subject,
      message,
      recipients,
      segment,
      selectedImage,
      addToast,
      handleRemoveSelectedImage,
    ],
  );

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="primary">
            <h4 className={classes.cardTitleWhite}>Email Marketing</h4>
            <p className={classes.cardCategoryWhite}>
              Disparo de campanhas promocionais para clientes cadastrados.
            </p>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Box mt={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form className={classes.form} onSubmit={handleSendCampaign}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  className={classes.formControl}
                  style={{ marginTop: 15 }}
                >
                  <InputLabel id="segment-label">Segmento</InputLabel>
                  <Select
                    labelId="segment-label"
                    label="Segmento"
                    name="segment"
                    value={segment}
                    onChange={event => setSegment(event.target.value)}
                  >
                    <MenuItem value="all">Todos os clientes</MenuItem>
                    <MenuItem value="pf">Somente Pessoa Fisica</MenuItem>
                    <MenuItem value="pj">Somente Pessoa Juridica</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  id="subject"
                  label="Assunto"
                  name="subject"
                  value={subject}
                  onChange={event => setSubject(event.target.value)}
                />

                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  multiline
                  rows={8}
                  id="message"
                  label="Mensagem"
                  name="message"
                  value={message}
                  onChange={event => setMessage(event.target.value)}
                />

                <Box mt={2}>
                  <p style={{ marginBottom: 10 }}>
                    Foto para o email (opcional)
                  </p>

                  <FileUploader
                    accept="image/*"
                    onFileSelectSuccess={handleImageSelect}
                    onFileSelectError={({ error }) =>
                      addToast(error, {
                        appearance: 'error',
                        autoDismiss: true,
                      })
                    }
                  />

                  {selectedImage ? (
                    <Box mt={2}>
                      <p style={{ marginBottom: 10 }}>
                        Arquivo selecionado: <strong>{selectedImage.name}</strong>
                      </p>

                      {imagePreviewUrl ? (
                        <img
                          src={imagePreviewUrl}
                          alt="Pre-visualizacao da foto da campanha"
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: 280,
                            borderRadius: 4,
                          }}
                        />
                      ) : null}

                      <Button
                        color="danger"
                        type="button"
                        onClick={handleRemoveSelectedImage}
                        style={{ marginTop: 15 }}
                      >
                        Remover foto
                      </Button>
                    </Box>
                  ) : null}
                </Box>

                <p style={{ marginTop: 10 }}>
                  Destinatarios previstos: <strong>{recipients.length}</strong>
                </p>

                <Button
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                  style={{ marginTop: 20 }}
                >
                  {isSubmitting ? 'Enviando campanha...' : 'Enviar campanha'}
                </Button>
              </form>
            )}
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
